import { useState } from "react";
import { useNavigate } from "react-router";

import {
  getPermissions,
  getScopes,
  type PermissionType,
  type PermissionValue,
} from "entities/permissions";
import {
  AppPasswordSection,
  isAppPasswordValid,
  normalizeAppPassword,
} from "features/app-password";
import { useAddICloudAccountMutation } from "shared/api/use-add-icloud-account-mutation";
import { useEditAccountMutation } from "shared/api/use-edit-account-mutation";
import { Button } from "shared/ui/button";
import { Input } from "shared/ui/input";
import { Label } from "shared/ui/label";
import { Notification } from "shared/ui/notification";
import { ProgressLine } from "shared/ui/progress-line";
import { InstructionsField } from "widgets/instructions-field";
import { Permissions } from "widgets/permissions";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const STEP_COUNT = 4;

export const AppleConnect = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string>();
  const [instructions, setInstructions] = useState("");
  const [permissions, setPermissions] = useState(
    getPermissions([], "icloud").map((p) => ({
      ...p,
      value: "Full Access" as PermissionValue,
    }))
  );

  const addMutation = useAddICloudAccountMutation();
  const editMutation = useEditAccountMutation();

  const isEmailValid = emailRegex.test(email);

  const handlePermissionChange = (
    permission: PermissionType,
    newValue: PermissionValue
  ) => {
    setPermissions((permissions) => {
      return permissions.map((p) => {
        if (p.title === permission.title) {
          return { ...p, value: newValue };
        }
        return p;
      });
    });
  };

  const handleNext = async () => {
    if (step === 2) {
      try {
        setPasswordError(undefined);
        await addMutation.mutateAsync({
          email,
          appSpecificPassword: normalizeAppPassword(password),
          instructions: "",
          label: "",
          scopes: getScopes(permissions, "icloud"),
        });
        setStep(3);
      } catch (error) {
        console.error(error);
        setPasswordError("Password is not valid for this account");
      }
      return;
    }
    setStep((step) => step + 1);
  };

  const handlePrev = () => {
    if (step === 0) return navigate("/accounts");
    setStep((step) => step - 1);
  };

  const handleFinish = async () => {
    try {
      if (instructions.trim()) {
        await editMutation.mutateAsync({ type: "icloud", email, instructions });
      }
    } catch (error) {
      console.error(error);
    }
    navigate("/accounts");
  };

  const isNextDisabled =
    (step === 0 && !isEmailValid) ||
    (step === 2 && !isAppPasswordValid(password));

  return (
    <>
      <ProgressLine activeStepIndex={step} count={STEP_COUNT} className="mb-3" />

      <Label text="Email">
        <Input
          enterKeyHint="done"
          placeholder="user@icloud.com"
          value={email}
          disabled={step > 0}
          error={email && !isEmailValid ? "Email is not valid" : undefined}
          onChange={(e) => setEmail(e.target.value.trim())}
        />
      </Label>

      {step >= 1 && (
        <Permissions
          className="mt-5"
          permissions={permissions}
          onChange={handlePermissionChange}
        />
      )}

      {step >= 2 && (
        <AppPasswordSection
          className="mt-5"
          description="An app-specific password lets Resonar securely access your Apple calendar, contacts and mail. It is not your Apple ID password."
          learnMoreUrl="https://support.apple.com/102654"
          value={password}
          error={passwordError}
          onChange={(value) => {
            setPasswordError(undefined);
            setPassword(value);
          }}
        />
      )}

      {step === 3 && (
        <InstructionsField
          className="mt-5"
          instructions={instructions}
          onChange={setInstructions}
        />
      )}

      {step === 0 && (
        <Notification className="mt-3">
          <div>
            <span className="text-clear-blue">1/4.</span> Enter the email of
            the Apple account you want to connect.
          </div>
        </Notification>
      )}

      {step === 1 && (
        <Notification className="mt-3">
          <div>
            <span className="text-clear-blue">2/4.</span> Select which tools and
            data Resonar can access for this account.
          </div>
        </Notification>
      )}

      {step < 3 && (
        <div className="mt-auto flex w-full gap-x-3 pt-4">
          <Button
            size="52"
            className="w-full"
            theme="secondary"
            onClick={handlePrev}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          <Button
            disabled={isNextDisabled}
            isLoading={addMutation.isPending}
            size="52"
            className="w-full"
            theme="white"
            onClick={handleNext}
          >
            Next Step
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="mt-auto flex w-full pt-4">
          <Button
            size="52"
            theme="white"
            className="w-full"
            isLoading={editMutation.isPending}
            onClick={handleFinish}
          >
            Finish
          </Button>
        </div>
      )}
    </>
  );
};
